/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Listables is a collection of Listable(s)
 */

import { boxToQueryBounds } from '@zcode/nr-utils'
import type {
  Listables,
  ListablesApiResponse,
  NearbyListablesInnerApiResponse,
  RawListable,
} from '@zcode/state-type'
import type { Mutable } from '@zcode/utility-types'
import Listable from '../../shared/bizos/listable'

export const DEFAULT_LISTABLES: Listables = {
  featured: [],
  jobId: 0,
  listables: [],
  matching: 0,
  nearby: null,
  query: null, // TODO: available in the context where the API call was made
  spotlight: [],
}

const createNearbyListables = (nearby: NearbyListablesInnerApiResponse | null) => {
  return nearby
    ? {
        box: Array.isArray(nearby.box) ? boxToQueryBounds(nearby.box) : nearby.box,
        listables: nearby.listables
          ? nearby.listables.map((listable) => Listable.fromJS(listable))
          : [],
        matching: nearby.matching,
      }
    : null
}

// TODO(nico/yewon):
// this entire approach is somewhat "wrong". a bizo is not supposed to copy/map data until it is
// accessed. this is another weirdo "schema" incorrectly placed in the bizo module.

function fromJSArray(data: RawListable[]) {
  return data.map((listable) => Listable.fromJS(listable))
}

function fromJSListables(data: ListablesApiResponse): Listables {
  const listables: Pick<Listables, 'jobId' | 'matching' | 'query'> & {
    moreResult?: false
  } & Mutable<Pick<Listables, 'spotlight' | 'featured' | 'listables' | 'listingCount' | 'nearby'>> =
    {
      ...DEFAULT_LISTABLES,
      jobId: data.jobId,
      matching: data.matching,
      query: data.query ?? null,
    }

  // no result came back, set moreResult to be false to avoid unnecessary request
  if ((!data.listables && !data.featured) || !data.listables.length) {
    // TODO: add this to the bizo; set it for every result
    listables.moreResult = false
  }
  if (data.listables) {
    listables.listables = data.listables.map((listable) =>
      Listable.fromJS({
        is_featured: false,
        ...listable,
      })
    )
  }
  if (data.featured) {
    listables.featured = data.featured.map((listable) =>
      Listable.fromJS({
        // NOTE: we are forcing this listable to have a featured property. See
        // the listable bizo for more information.
        is_featured: true,
        ...listable,
      })
    )
  }
  if (data.nearby) {
    listables.nearby = {
      matches: createNearbyListables(data.nearby.matches),
      popular: createNearbyListables(data.nearby.popular),
    }
  }
  if (data.listing_count) {
    listables.listingCount = data.listing_count
  }
  return listables
}

function fromJS(data: ListablesApiResponse): ReturnType<typeof fromJSListables>
function fromJS(data: RawListable[]): ReturnType<typeof fromJSArray>
function fromJS(
  data?: ListablesApiResponse | RawListable[] | null | undefined
): ReturnType<typeof fromJSArray> | ReturnType<typeof fromJSListables>
function fromJS(data?: ListablesApiResponse | RawListable[] | null | undefined) {
  if (data) {
    // NOTE: data might be a lot of things!
    // - before an API call it is the query object
    // - after an API call it is the raw result
    // - during client initialization it is a Listables
    // - sometimes it is a plain array

    // NOTE: When data is a plain array we want to return an array, not a Listables.
    // TODO: We should consider moving this to its own bizo...
    if (Array.isArray(data)) {
      return fromJSArray(data)
    }

    return fromJSListables(data)
  }
  return DEFAULT_LISTABLES
}

// eslint-disable-next-line import/no-default-export,import/no-anonymous-default-export
export default { fromJS }
